import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../api/client';

function generateId() {
  return 'r' + Math.random().toString(36).slice(2, 10);
}

const EMPTY = { id: '', name: '', origin: '', rating: 5, text: '', date: '', visible: true };

export default function ReviewsManager() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch('/reviews.php?all=1')
      .then(data => {
        setReviews(data.reviews || []);
        setLoading(false);
      })
      .catch(e => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    const blank = { ...EMPTY, id: generateId() };
    setEditingId('__new__');
    setForm(blank);
  };

  const startEdit = (review) => {
    setEditingId(review.id);
    setForm({ ...review });
  };

  const cancel = () => {
    setEditingId(null);
    setForm(null);
  };

  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const save = async () => {
    if (!form.name.trim() || !form.text.trim()) {
      alert('Name und Text sind Pflichtfelder.');
      return;
    }
    setSaving(true);
    try {
      await apiFetch('/reviews.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save', review: form }),
      });
      cancel();
      load();
    } catch (e) {
      alert('Fehler beim Speichern: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm('Bewertung wirklich löschen?')) return;
    try {
      await apiFetch('/reviews.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', id }),
      });
      load();
    } catch (e) {
      alert('Fehler beim Löschen: ' + e.message);
    }
  };

  if (loading) return <p style={styles.info}>Lade Bewertungen...</p>;
  if (error) return <p style={styles.errorText}>Fehler: {error}</p>;

  return (
    <div style={styles.manager}>
      <div style={styles.topBar}>
        <span style={styles.count}>{reviews.length} Bewertung{reviews.length !== 1 ? 'en' : ''}</span>
        {editingId !== '__new__' && (
          <button style={styles.addBtn} onClick={startNew}>+ Neue Bewertung</button>
        )}
      </div>

      {/* New review form */}
      {editingId === '__new__' && (
        <div style={{ ...styles.card, ...styles.cardEditing }}>
          <h4 style={styles.cardEditTitle}>Neue Bewertung</h4>
          <ReviewForm form={form} setField={setField} />
          <div style={styles.formActions}>
            <button style={styles.saveBtn} onClick={save} disabled={saving}>
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
            <button style={styles.cancelBtn} onClick={cancel}>Abbrechen</button>
          </div>
        </div>
      )}

      {/* Existing reviews */}
      {reviews.length === 0 && editingId !== '__new__' && (
        <p style={styles.empty}>Noch keine Bewertungen. Fügen Sie die erste hinzu!</p>
      )}

      {reviews.map(review => (
        <div key={review.id} style={editingId === review.id ? { ...styles.card, ...styles.cardEditing } : styles.card}>
          {editingId === review.id ? (
            <>
              <h4 style={styles.cardEditTitle}>Bewertung bearbeiten</h4>
              <ReviewForm form={form} setField={setField} />
              <div style={styles.formActions}>
                <button style={styles.saveBtn} onClick={save} disabled={saving}>
                  {saving ? 'Speichern...' : 'Speichern'}
                </button>
                <button style={styles.cancelBtn} onClick={cancel}>Abbrechen</button>
              </div>
            </>
          ) : (
            <>
              <div style={styles.readHeader}>
                <div style={styles.readLeft}>
                  <span style={styles.readStars}>{'★'.repeat(review.rating || 5)}{'☆'.repeat(5 - (review.rating || 5))}</span>
                  <strong style={styles.readName}>{review.name}</strong>
                  {review.origin && <span style={styles.readOrigin}> · {review.origin}</span>}
                  {review.date && <span style={styles.readDate}> · {review.date}</span>}
                </div>
                <div style={styles.readBadges}>
                  <span style={review.visible ? styles.badgeVisible : styles.badgeHidden}>
                    {review.visible ? 'Sichtbar' : 'Versteckt'}
                  </span>
                </div>
              </div>
              <p style={styles.readText}>„{review.text}"</p>
              <div style={styles.readActions}>
                <button style={styles.editBtn} onClick={() => startEdit(review)}>Bearbeiten</button>
                <button style={styles.deleteBtn} onClick={() => del(review.id)}>Löschen</button>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function ReviewForm({ form, setField }) {
  return (
    <div style={formStyles.grid}>
      <Field label="Name *" value={form.name} onChange={v => setField('name', v)} />
      <Field label="Herkunft (z.B. Wien, Österreich)" value={form.origin} onChange={v => setField('origin', v)} />
      <div style={formStyles.group}>
        <label style={formStyles.label}>Bewertung *</label>
        <div style={formStyles.stars}>
          {[1, 2, 3, 4, 5].map(n => (
            <button
              key={n}
              type="button"
              style={{ ...formStyles.starBtn, color: n <= form.rating ? '#f59e0b' : '#d1d5db' }}
              onClick={() => setField('rating', n)}
            >★</button>
          ))}
          <span style={formStyles.ratingLabel}>{form.rating}/5</span>
        </div>
      </div>
      <Field label="Datum (z.B. August 2024)" value={form.date} onChange={v => setField('date', v)} />
      <div style={{ ...formStyles.group, gridColumn: '1 / -1' }}>
        <label style={formStyles.label}>Bewertungstext *</label>
        <textarea
          value={form.text}
          onChange={e => setField('text', e.target.value)}
          rows={4}
          style={formStyles.textarea}
          placeholder="Was hat Ihnen besonders gefallen?"
        />
      </div>
      <div style={formStyles.group}>
        <label style={formStyles.label}>Sichtbarkeit</label>
        <label style={formStyles.toggle}>
          <input
            type="checkbox"
            checked={form.visible}
            onChange={e => setField('visible', e.target.checked)}
            style={{ marginRight: '0.5rem' }}
          />
          Auf der Website anzeigen
        </label>
      </div>
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <div style={formStyles.group}>
      <label style={formStyles.label}>{label}</label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={formStyles.input}
      />
    </div>
  );
}

const styles = {
  manager: { display: 'flex', flexDirection: 'column', gap: '0.85rem' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' },
  count: { color: '#666', fontFamily: 'Arial, sans-serif', fontSize: '0.9rem' },
  addBtn: { backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '5px', padding: '0.6rem 1.2rem', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: '0.9rem', fontWeight: '600' },

  card: { backgroundColor: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '1.25rem' },
  cardEditing: { border: '2px solid #28a745', backgroundColor: '#f8fff9' },
  cardEditTitle: { color: '#333', fontFamily: 'Arial, sans-serif', fontSize: '0.95rem', fontWeight: '700', marginBottom: '1rem' },

  readHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '0.6rem', flexWrap: 'wrap' },
  readLeft: { display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' },
  readStars: { color: '#f59e0b', fontSize: '1rem', letterSpacing: '1px' },
  readName: { fontFamily: 'Arial, sans-serif', fontSize: '0.9rem', color: '#222' },
  readOrigin: { fontFamily: 'Arial, sans-serif', fontSize: '0.85rem', color: '#888' },
  readDate: { fontFamily: 'Arial, sans-serif', fontSize: '0.85rem', color: '#aaa' },
  readBadges: { display: 'flex', gap: '0.4rem' },
  badgeVisible: { backgroundColor: '#d1fae5', color: '#065f46', borderRadius: '4px', padding: '0.15rem 0.55rem', fontSize: '0.78rem', fontFamily: 'Arial, sans-serif', fontWeight: '600' },
  badgeHidden: { backgroundColor: '#f3f4f6', color: '#6b7280', borderRadius: '4px', padding: '0.15rem 0.55rem', fontSize: '0.78rem', fontFamily: 'Arial, sans-serif', fontWeight: '600' },
  readText: { color: '#555', fontFamily: 'Arial, sans-serif', fontSize: '0.9rem', lineHeight: '1.6', fontStyle: 'italic', marginBottom: '0.85rem' },
  readActions: { display: 'flex', gap: '0.5rem' },

  formActions: { display: 'flex', gap: '0.75rem', marginTop: '1rem' },
  saveBtn: { backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '5px', padding: '0.6rem 1.4rem', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: '0.9rem', fontWeight: '700' },
  cancelBtn: { backgroundColor: 'transparent', color: '#666', border: '1px solid #ccc', borderRadius: '5px', padding: '0.6rem 1.2rem', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: '0.9rem' },
  editBtn: { backgroundColor: 'transparent', color: '#0066cc', border: '1px solid #0066cc', borderRadius: '4px', padding: '0.3rem 0.8rem', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: '0.82rem' },
  deleteBtn: { backgroundColor: 'transparent', color: '#dc3545', border: '1px solid #dc3545', borderRadius: '4px', padding: '0.3rem 0.8rem', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: '0.82rem' },

  empty: { color: '#999', fontFamily: 'Arial, sans-serif', textAlign: 'center', padding: '2rem' },
  info: { color: '#666', fontFamily: 'Arial, sans-serif' },
  errorText: { color: '#dc3545', fontFamily: 'Arial, sans-serif' },
};

const formStyles = {
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' },
  group: { display: 'flex', flexDirection: 'column', gap: '0.3rem' },
  label: { fontSize: '0.82rem', fontWeight: '600', color: '#555', fontFamily: 'Arial, sans-serif' },
  input: { padding: '0.5rem 0.7rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', fontFamily: 'Arial, sans-serif' },
  textarea: { padding: '0.5rem 0.7rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', fontFamily: 'Arial, sans-serif', resize: 'vertical' },
  stars: { display: 'flex', gap: '0.2rem', alignItems: 'center' },
  starBtn: { background: 'none', border: 'none', fontSize: '1.6rem', cursor: 'pointer', padding: '0', lineHeight: 1 },
  ratingLabel: { color: '#666', fontFamily: 'Arial, sans-serif', fontSize: '0.82rem', marginLeft: '0.3rem' },
  toggle: { display: 'flex', alignItems: 'center', fontFamily: 'Arial, sans-serif', fontSize: '0.9rem', color: '#333', cursor: 'pointer' },
};
